import { OTHER_PURPOSE } from '@Constants/otherPaymentType.constants';

/**
 * Roles that represent the person who actually owns (or will own) the
 * underlying property in the transaction. Used to decide whether a "home"
 * affordance (e.g. the property-selector pill's house icon) is meaningful
 * for the signed-in participant. Anyone else on the deal — agent, attorney,
 * lender, title company, etc. — never owns the property, so the house icon
 * would be visual noise.
 *
 * Values mirror the canonical ROLE_OPTIONS in the staff portal's
 * src/constants/newDeal.constants.js. Comparison is case-insensitive
 * to defend against any drift in casing across services.
 */
const PROPERTY_OWNER_ROLES = new Set(['buyer', 'seller']);

export const isPropertyOwnerRole = (role: string | null | undefined): boolean => {
    if (!role) return false;
    return PROPERTY_OWNER_ROLES.has(role.trim().toLowerCase());
};

/**
 * Role strings reach us from staff-entered data with casing and spacing drift,
 * so every non-alphanumeric character is stripped before comparison. A plain
 * lowercase comparison silently drops a real share of buyers.
 */
const normalizePaymentRole = (role: string | null | undefined): string =>
    (role ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Buyers may start every purpose the BFF offers.
const ALL_PURPOSES_ROLES = new Set(['buyer']);

// Sellers receive the money, so they may start only an Other payment.
const OTHER_PURPOSE_ONLY_ROLES = new Set(['seller']);

// The BFF applies no role gate to participantInitiablePurposes, so this is the only check.
export const canRoleInitiatePurpose = (role: string | null | undefined, purpose: string): boolean => {
    const normalized = normalizePaymentRole(role);
    if (ALL_PURPOSES_ROLES.has(normalized)) return true;
    return OTHER_PURPOSE_ONLY_ROLES.has(normalized) && purpose === OTHER_PURPOSE;
};

export const isPaymentInitiatorRole = (role: string | null | undefined): boolean => {
    const normalized = normalizePaymentRole(role);
    return ALL_PURPOSES_ROLES.has(normalized) || OTHER_PURPOSE_ONLY_ROLES.has(normalized);
};
